import { useUser } from "@clerk/clerk-react";
import {
  BugReport as BugReportIcon,
  Dashboard as DashboardIcon,
  PlayArrow as PlayArrowIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  QueryStats as QueryStatsIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { Box, Button, CircularProgress, Stack, Tab, Tabs, Tooltip } from "@mui/material";
import { useCallback, useMemo } from "react";
import { Link } from "react-router";
import { PageHeader } from "../../components/page-header";
import { useApiQuery } from "../../hooks/use-api-query";
import { useQueryParams } from "../../hooks/use-query-params";
import type { AnalyticsPeriod, AnalyticsResponse } from "../../types";
import { CoverageTab } from "./components/coverage-tab";
import { DateRangeFilter, type DateRangeValue } from "./components/date-range-filter";
import { FlakinessTab } from "./components/flakiness-tab";
import { OverviewTab } from "./components/overview-tab";
import { SuiteRunsTab } from "./components/suite-runs-tab";
import { TestRunsTab } from "./components/test-runs-tab";
import { TimeSeriesTab } from "./components/time-series-tab";

const TAB_NAMES = [
  "overview",
  "test-runs",
  "suite-runs",
  "flakiness",
  "coverage",
  "time-series",
] as const;
type TabName = (typeof TAB_NAMES)[number];

function getTabIndex(tabName: string | undefined): number {
  const index = TAB_NAMES.indexOf(tabName as TabName);
  return index >= 0 ? index : 0;
}

function getTabName(index: number): TabName {
  return TAB_NAMES[index] ?? "overview";
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function buildQueryString(dateRange: DateRangeValue): string {
  const params = new URLSearchParams();
  if (dateRange.startDate && dateRange.endDate) {
    params.set("start_date", dateRange.startDate);
    params.set("end_date", dateRange.endDate);
  } else if (dateRange.period) {
    params.set("period", dateRange.period);
  }
  return params.toString();
}

const VALID_PERIODS: AnalyticsPeriod[] = ["7d", "30d", "90d", "all"];

export default function AnalyticsPage() {
  const { user } = useUser();
  const { getParam, setParams } = useQueryParams();
  const activeTab = getTabIndex(getParam("tab"));

  // Derive dateRange from query params
  const dateRange = useMemo<DateRangeValue>(() => {
    const period = getParam("period");
    const startDate = getParam("startDate");
    const endDate = getParam("endDate");

    if (startDate && endDate) {
      return { startDate, endDate };
    }
    if (period && VALID_PERIODS.includes(period as AnalyticsPeriod)) {
      return { period: period as AnalyticsPeriod };
    }
    return { period: "30d" };
  }, [getParam]);

  const setDateRange = useCallback(
    (value: DateRangeValue) => {
      if (value.period) {
        // Period-based: remove custom dates, set period (omit if default "30d")
        setParams({
          period: value.period === "30d" ? undefined : value.period,
          startDate: undefined,
          endDate: undefined,
        });
      } else if (value.startDate && value.endDate) {
        // Custom range: remove period, set dates
        setParams({
          period: undefined,
          startDate: value.startDate,
          endDate: value.endDate,
        });
      }
    },
    [setParams],
  );

  const queryString = useMemo(() => buildQueryString(dateRange), [dateRange]);

  const { data, isLoading } = useApiQuery<AnalyticsResponse>(`/dashboard/analytics?${queryString}`);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const tabName = getTabName(newValue);
    setParams({ tab: tabName === "overview" ? undefined : tabName });
  };

  return (
    <Stack data-testid="analytics-page" spacing={2} sx={{ pb: 2, mx: 1 }}>
      <PageHeader
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <QueryStatsIcon color="primary" />
            <span>Analytics</span>
          </Stack>
        }
        subtitle={`Welcome ${user?.fullName ?? "there"}. Organization-wide quality and execution analytics.`}
        actions={
          <Stack direction="row" spacing={2} alignItems="center">
            <DateRangeFilter value={dateRange} onChange={setDateRange} />
            <Tooltip title="Run all tests (opens suite runs)">
              <Button
                component={Link}
                to="/tests-main/test-suites"
                variant="gradient"
                size="small"
                startIcon={<PlayArrowIcon fontSize="small" />}
                sx={{
                  height: 32,
                  borderRadius: 2,
                  px: 1.5,
                  minWidth: "auto",
                  lineHeight: 1,
                }}
              >
                Run Tests
              </Button>
            </Tooltip>
          </Stack>
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="analytics tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            icon={<DashboardIcon fontSize="small" />}
            iconPosition="start"
            label="Overview"
            id="analytics-tab-0"
            aria-controls="analytics-tabpanel-0"
          />
          <Tab
            icon={<SpeedIcon fontSize="small" />}
            iconPosition="start"
            label="Test Runs"
            id="analytics-tab-1"
            aria-controls="analytics-tabpanel-1"
          />
          <Tab
            icon={<PlaylistAddCheckIcon fontSize="small" />}
            iconPosition="start"
            label="Suite Runs"
            id="analytics-tab-2"
            aria-controls="analytics-tabpanel-2"
          />
          <Tab
            icon={<BugReportIcon fontSize="small" />}
            iconPosition="start"
            label="Flakiness"
            id="analytics-tab-3"
            aria-controls="analytics-tabpanel-3"
          />
          <Tab
            icon={<VisibilityIcon fontSize="small" />}
            iconPosition="start"
            label="Coverage"
            id="analytics-tab-4"
            aria-controls="analytics-tabpanel-4"
          />
          <Tab
            icon={<TimelineIcon fontSize="small" />}
            iconPosition="start"
            label="Time Series"
            id="analytics-tab-5"
            aria-controls="analytics-tabpanel-5"
          />
        </Tabs>
      </Box>

      {isLoading && !data && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      <TabPanel value={activeTab} index={0}>
        <OverviewTab data={data?.overview} isLoading={isLoading} />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <TestRunsTab data={data?.test_runs} isLoading={isLoading} />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <SuiteRunsTab data={data?.suite_runs} isLoading={isLoading} />
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <FlakinessTab data={data?.flakiness} isLoading={isLoading} />
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <CoverageTab data={data?.coverage} isLoading={isLoading} />
      </TabPanel>

      <TabPanel value={activeTab} index={5}>
        <TimeSeriesTab dateRange={dateRange} />
      </TabPanel>
    </Stack>
  );
}
